import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { TenantViewModel } from '../api';

export function SeedConfirmDialog({
  tenant,
  onCancel,
  onConfirm,
  pending,
  submitError,
}: {
  readonly tenant: TenantViewModel | null;
  readonly onCancel: () => void;
  readonly onConfirm: (tenant: TenantViewModel) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const open = tenant !== null;
  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('routes.platform.tenants.seed.title')}</DialogTitle>
          <DialogDescription>{t('routes.platform.tenants.seed.description')}</DialogDescription>
        </DialogHeader>
        {submitError !== undefined && submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t('routes.platform.tenants.seed.error', { code: submitErrorCode })}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            {t('routes.platform.tenants.seed.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={pending}
            onClick={() => {
              if (tenant !== null) onConfirm(tenant);
            }}
          >
            {t('routes.platform.tenants.seed.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
